import {
    DefaultButton,
    Dialog, DialogFooter,
    DialogType, Stack, Dropdown, IDropdownOption
} from "@fluentui/react";
import React, {useEffect, useState} from "react";
import NumberField from "../Inputs/NumberField";
import ToggleField from '../Common/ToggleField';
import { IStorefrontSectionEditorModalProps, StyleField } from "./StorefrontSectionStyleEditorModal";
import Accordion from "../Common/Accordion";

const hAlignmentOptions: IDropdownOption[] = [
    { key: 'start', text: 'Start' },
    { key: 'end', text: 'End' },
    { key: 'center', text: 'Center' },
    { key: 'justify', text: 'Justify' }
];
const vAlignmentOptions: IDropdownOption[] = [
    { key: 'baseline', text: 'Baseline' },
    { key: 'sub', text: 'Subscript' },
    { key: 'super', text: 'Superscript' },
    { key: 'text-top', text: 'Text Top' },
    { key: 'text-bottom', text: 'Text Top' },
    { key: 'middle', text: 'Middle' },
    { key: 'top', text: 'Top' },
    { key: 'bottom', text: 'Bottom' }
];

const unitOptions: IDropdownOption[] = [
    { key: 'px', text: 'px' },
    { key: '%', text: '%' }
];

const StorefrontSectionLayoutEditorModal: React.FC<IStorefrontSectionEditorModalProps> = props => {
    const [granularPadding, setGranularPadding] = useState(false);
    const [granularMargin, setGranularMargin] = useState(false);

    const [detailedDimensions, setDetailedDimensions] = useState(false);
    const [width, setWidth] = useState<number | undefined>(props.css?.width ? parseInt(props.css.width) : undefined);
    const [widthUnit, setWidthUnit] = useState<'px' | '%'>(props.css?.width && props.css.width.toString().includes('%') ? '%' : 'px');
    const [height, setHeight] = useState<number | undefined>(props.css?.height ? parseInt(props.css.height) : undefined);
    const [heightUnit, setHeightUnit] = useState<'px' | '%'>(props.css?.height && props.css.height.toString().includes('%') ? '%' : 'px');
    const [minWidth, setMinWidth] = useState<number | undefined>(props.css?.minWidth ? parseInt(props.css.minWidth) : undefined);
    const [minWidthUnit, setMinWidthUnit] = useState<'px' | '%'>(props.css?.minWidth && props.css.minWidth.toString().includes('%') ? '%' : 'px');
    const [maxWidth, setMaxWidth] = useState<number | undefined>(props.css?.maxWidth ? parseInt(props.css.maxWidth) : undefined);
    const [maxWidthUnit, setMaxWidthUnit] = useState<'px' | '%'>((props.css?.maxWidth && props.css.maxWidth.toString().includes('%')) ? '%' : 'px');
    const [minHeight, setMinHeight] = useState<number | undefined>(props.css?.minHeight ? parseInt(props.css.minHeight) : undefined);
    const [minHeightUnit, setMinHeightUnit] = useState<'px' | '%'>((props.css?.minHeight && props.css.minHeight.toString().includes('%')) ? '%' : 'px');
    const [maxHeight, setMaxHeight] = useState<number | undefined>(props.css?.maxHeight ? parseInt(props.css.maxHeight) : undefined);
    const [maxHeightUnit, setMaxHeightUnit] = useState<'px' | '%'>((props.css?.maxHeight && props.css.maxHeight.toString().includes('%')) ? '%' : 'px');

    // Update parent on change
    useEffect(() => {
        props.onChange?.('width', width !== undefined ? `${width}${widthUnit}` : undefined);
    }, [width, widthUnit]);
    useEffect(() => {
        props.onChange?.('height', height !== undefined ? `${height}${heightUnit}` : undefined);
    }, [height, heightUnit]);
    useEffect(() => {
        props.onChange?.('minWidth', detailedDimensions && minWidth !== undefined ? `${minWidth}${minWidthUnit}` : undefined);
    }, [minWidth, minWidthUnit, detailedDimensions]);
    useEffect(() => {
        props.onChange?.('maxWidth', detailedDimensions && maxWidth !== undefined ? `${maxWidth}${maxWidthUnit}` : undefined);
    }, [maxWidth, maxWidthUnit, detailedDimensions]);
    useEffect(() => {
        props.onChange?.('minHeight', detailedDimensions && minHeight !== undefined ? `${minHeight}${minHeightUnit}` : undefined);
    }, [minHeight, minHeightUnit, detailedDimensions]);
    useEffect(() => {
        props.onChange?.('maxHeight', detailedDimensions && maxHeight !== undefined ? `${maxHeight}${maxHeightUnit}` : undefined);
    }, [maxHeight, maxHeightUnit, detailedDimensions]);

    const overflowOptions: IDropdownOption[] = [
        {
            key: 'visible',
            text: 'Visible',
        },
        {
            key: 'hidden',
            text: 'Hidden',
        },
        {
            key: 'clip',
            text: 'Clip',
        },
        {
            key: 'scroll',
            text: 'Scroll',
        },
        {
            key: 'auto',
            text: 'Auto',
        }
    ]

    return (
        <Dialog
            hidden={!props.displayModal}
            onDismiss={props.closer}
            dialogContentProps={{
                type: DialogType.largeHeader,
                title: 'Modify Layout',
            }}
            modalProps={{
                isBlocking: true,
            }}
        >
            {props.css ?
                <Stack tokens={{ childrenGap: 12 }}>
                    <Accordion beginExpanded={true} useChevron={true} title={'Dimensions'}>
                        <ToggleField inline={true} label="Detailed Dimension Settings" checked={detailedDimensions} onChange={(e, v) => setDetailedDimensions(!!v)} />
                        {/* Width */}
                        <Stack horizontal tokens={{ childrenGap: 8 }}>
                            <NumberField
                                label="Width" 
                                value={width} 
                                onChange={( val) => setWidth(val)} 
                                onRenderSuffix={() =>
                                    <Dropdown options={unitOptions} selectedKey={widthUnit} onChange={(e, v) => setWidthUnit(v?.key as 'px' | '%')} />
                                }
                            />
                        </Stack>
                        {/* Height */}
                        <Stack horizontal tokens={{ childrenGap: 8 }}>
                            <NumberField
                                label="Height"
                                value={height}
                                onChange={val => setHeight(val)}
                                onRenderSuffix={() =>
                                    <Dropdown options={unitOptions} selectedKey={heightUnit} onChange={(e, v) => setHeightUnit(v?.key as 'px' | '%')} />
                                }
                            />
                        </Stack>
                        {detailedDimensions && (
                            <React.Fragment>
                                {/* Min Width */}
                                <Stack horizontal tokens={{ childrenGap: 8 }}>
                                    <NumberField
                                        label="Min Width"
                                        onRenderSuffix={() => 
                                            <Dropdown options={unitOptions} selectedKey={minWidthUnit} onChange={(e, v) => setMinWidthUnit(v?.key as 'px' | '%')} />
                                        }
                                        value={minWidth} 
                                        onChange={val => setMinWidth(val)} 
                                    />
                                </Stack>
                                {/* Max Width */}
                                <Stack horizontal tokens={{ childrenGap: 8 }}>
                                    <NumberField
                                        label="Max Width"
                                        onRenderSuffix={() => 
                                            <Dropdown options={unitOptions} selectedKey={maxWidthUnit} onChange={(e, v) => setMaxWidthUnit(v?.key as 'px' | '%')} />
                                        }
                                        value={maxWidth} 
                                        onChange={val => setMaxWidth(val)} 
                                    />
                                </Stack>
                                {/* Min Height */}
                                <Stack horizontal tokens={{ childrenGap: 8 }} style={{alignItems: 'center'}}>
                                    <NumberField
                                        onRenderSuffix={() => 
                                            <Dropdown options={unitOptions} selectedKey={minHeightUnit} onChange={(e, v) => setMinHeightUnit(v?.key as 'px' | '%')} />
                                        } 
                                        label="Min Height"
                                        value={minHeight} 
                                        onChange={val => setMinHeight(val)} 
                                    />
                                </Stack>
                                {/* Max Height */}
                                <Stack horizontal tokens={{ childrenGap: 8 }}>
                                    <NumberField 
                                        label="Max Height"
                                        value={maxHeight} 
                                        onChange={val => setMaxHeight(val)} 
                                        onRenderSuffix={() =>
                                            <Dropdown options={unitOptions} selectedKey={maxHeightUnit} onChange={(e, v) => setMaxHeightUnit(v?.key as 'px' | '%')} />
                                        }
                                    />
                                </Stack>
                            </React.Fragment>
                        )}
                    </Accordion>
                    <Accordion beginExpanded={true} useChevron={true} title={'Padding'}>
                        <ToggleField inline={true} label="Granular Padding" checked={granularPadding} onChange={(e, v) => setGranularPadding(!!v)} />
                        {!granularPadding ? (
                            <StyleField editor='layout' prop='padding'>
                                <NumberField suffix={'px'} label="Padding" value={props.css.padding} onChange={val => props.onChange?.('padding', val)} />
                            </StyleField>
                        ) : (
                            <React.Fragment>
                                <StyleField editor='layout' prop='paddingTop'>
                                    <NumberField suffix={'px'} label="Padding Top" value={props.css.paddingTop} onChange={val => props.onChange?.('paddingTop', val)} />
                                </StyleField>
                                <StyleField editor='layout' prop='paddingRight'>
                                    <NumberField suffix={'px'} label="Padding Right" value={props.css.paddingRight} onChange={val => props.onChange?.('paddingRight', val)} />
                                </StyleField>
                                <StyleField editor='layout' prop='paddingBottom'>
                                    <NumberField suffix={'px'} label="Padding Bottom" value={props.css.paddingBottom} onChange={val => props.onChange?.('paddingBottom', val)} />
                                </StyleField>
                                <StyleField editor='layout' prop='paddingLeft'>
                                    <NumberField suffix={'px'} label="Padding Left" value={props.css.paddingLeft} onChange={val => props.onChange?.('paddingLeft', val)} />
                                </StyleField>
                            </React.Fragment>
                        )}
                    </Accordion>
                    <Accordion beginExpanded={true} useChevron={true} title={'Margin'}>
                        <ToggleField inline={true} label="Granular Margin" checked={granularMargin} onChange={(e, v) => setGranularMargin(!!v)} />
                        {!granularMargin ? (
                            <StyleField editor='layout' prop='margin'>
                                <NumberField suffix={'px'} label="Margin" value={props.css.margin} onChange={val => props.onChange?.('margin', val)} />
                            </StyleField>
                        ) : (
                            <React.Fragment>
                                <StyleField editor='layout' prop='marginTop'>
                                    <NumberField suffix={'px'} label="Margin Top" value={props.css.marginTop} onChange={val => props.onChange?.('marginTop', val)} />
                                </StyleField>
                                <StyleField editor='layout' prop='marginRight'>
                                    <NumberField suffix={'px'} label="Margin Right" value={props.css.marginRight} onChange={val => props.onChange?.('marginRight', val)} />
                                </StyleField>
                                <StyleField editor='layout' prop='marginBottom'>
                                    <NumberField suffix={'px'} label="Margin Bottom" value={props.css.marginBottom} onChange={val => props.onChange?.('marginBottom', val)} />
                                </StyleField>
                                <StyleField editor='layout' prop='marginLeft'>
                                    <NumberField suffix={'px'} label="Margin Left" value={props.css.marginLeft} onChange={val => props.onChange?.('marginLeft', val)} />
                                </StyleField>
                            </React.Fragment>
                        )}
                    </Accordion>
                    <Accordion useChevron={true} title={'Overflow'}>
                        <StyleField editor='layout' prop='overflowX'>
                            <Dropdown options={overflowOptions} label={'Overflow X'} selectedKey={props.css.overflowX} onChange={(e, v) => props.onChange?.('overflowX', v?.key?.toString())}/>
                        </StyleField>
                        <StyleField editor='layout' prop='overflowY'>
                            <Dropdown options={overflowOptions} label={'Overflow Y'} selectedKey={props.css.overflowY} onChange={(e, v) => props.onChange?.('overflowY', v?.key?.toString())}/>
                        </StyleField>
                    </Accordion>
                    
                    <Accordion useChevron={true} title={'Alignment'}>
                        <StyleField editor='layout' prop='textAlign'>
                            <Dropdown label="Text Horizontal Alignment" options={hAlignmentOptions} selectedKey={props.css.textAlign} onChange={(e, val) => props.onChange?.('textAlign', val?.key)} />
                        </StyleField>
                        <StyleField editor='layout' prop='verticalAlign'>
                            <Dropdown label="Vertical Alignment" options={vAlignmentOptions} selectedKey={props.css.verticalAlign} onChange={(e, val) => props.onChange?.('verticalAlign', val?.key)} />
                        </StyleField>

                    </Accordion>
                    <div style={{border: '1px solid #ccc'}}>
                        <div style={{
                            width: '200px',
                            height: '100px',
                            padding: granularPadding ? `${props.css.paddingTop}px ${props.css.paddingRight}px ${props.css.paddingBottom}px ${props.css.paddingLeft}px` : `${props.css.padding}px`,
                            textAlign: props.css.alignment,
                            background: '#f5f5f5',
                            border: '1px solid #ccc',
                            margin: granularMargin ? `${props.css.marginTop}px ${props.css.marginRight}px ${props.css.marginBottom}px ${props.css.marginLeft}px` : `${props.css.margin}px`,
    
                        }}>
                            <span>Preview</span>
                        </div>
                    </div>
                </Stack> :
                <div>
                    Unable to load CSS
                </div>
            }
            <DialogFooter>
                <DefaultButton
                    primary={true}
                    text='Ok'
                    onClick={props.closer}
                />
            </DialogFooter>
        </Dialog>
    );
}
export default StorefrontSectionLayoutEditorModal;
